import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import PublishButton from '@/components/PublishButton';
import { publishTarget } from '@/utils/roles';
import { initLandingAnim } from '@/lib/landingAnim';
import '@/styles/landing.css';

const LANDING_HTML = `<!-- NAV -->
<nav class="nav">
  <a class="logo" href="#">
    <div class="logo-mark">C2</div>
    Colis<em>2</em>Ship
  </a>
  <ul class="nav-links">
    <li><a href="#" class="nav-link">Accueil</a></li>
    <li><a href="#" class="nav-link">Comment ça marche</a></li>
    <li><a href="#" class="nav-link">Trajets</a></li>
    <li><a href="#" class="nav-link">Afrique</a></li>
    <li><a href="#" class="nav-link">Réseau</a></li>
  </ul>
  <div class="nav-r" data-landing-actions></div>
</nav>

<!-- HERO -->
<div class="hero">
  <!-- SVG bg moderne : réseau de noeuds logistiques + routes -->
  <div class="hero-bg">
    <svg viewBox="0 0 1200 520" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="glow1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#7ecfc2" stop-opacity=".18"/>
          <stop offset="100%" stop-color="#7ecfc2" stop-opacity="0"/>
        </radialGradient>
        <radialGradient id="glow2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#F0C97A" stop-opacity=".12"/>
          <stop offset="100%" stop-color="#F0C97A" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <!-- Glows de fond -->
      <ellipse cx="900" cy="200" rx="360" ry="280" fill="url(#glow1)"/>
      <ellipse cx="200" cy="380" rx="260" ry="200" fill="url(#glow2)"/>
      <!-- Grille isométrique légère -->
      <g stroke="rgba(255,255,255,0.028)" stroke-width="1" fill="none">
        <line x1="0" y1="0" x2="1200" y2="0"/><line x1="0" y1="52" x2="1200" y2="52"/>
        <line x1="0" y1="104" x2="1200" y2="104"/><line x1="0" y1="156" x2="1200" y2="156"/>
        <line x1="0" y1="208" x2="1200" y2="208"/><line x1="0" y1="260" x2="1200" y2="260"/>
        <line x1="0" y1="312" x2="1200" y2="312"/><line x1="0" y1="364" x2="1200" y2="364"/>
        <line x1="0" y1="416" x2="1200" y2="416"/><line x1="0" y1="468" x2="1200" y2="468"/>
        <line x1="0" y1="520" x2="1200" y2="520"/>
        <line x1="0" y1="0" x2="0" y2="520"/><line x1="100" y1="0" x2="100" y2="520"/>
        <line x1="200" y1="0" x2="200" y2="520"/><line x1="300" y1="0" x2="300" y2="520"/>
        <line x1="400" y1="0" x2="400" y2="520"/><line x1="500" y1="0" x2="500" y2="520"/>
        <line x1="600" y1="0" x2="600" y2="520"/><line x1="700" y1="0" x2="700" y2="520"/>
        <line x1="800" y1="0" x2="800" y2="520"/><line x1="900" y1="0" x2="900" y2="520"/>
        <line x1="1000" y1="0" x2="1000" y2="520"/><line x1="1100" y1="0" x2="1100" y2="520"/>
        <line x1="1200" y1="0" x2="1200" y2="520"/>
      </g>
      <!-- Arcs de routes entre continents -->
      <g fill="none" stroke-width="1.2">
        <path d="M120,380 Q400,80 860,160" stroke="rgba(126,207,194,.18)" stroke-dasharray="6 4">
          <animate attributeName="stroke-dashoffset" from="0" to="-40" dur="3s" repeatCount="indefinite"/>
        </path>
        <path d="M200,320 Q550,60 950,200" stroke="rgba(240,201,122,.14)" stroke-dasharray="5 5">
          <animate attributeName="stroke-dashoffset" from="0" to="-40" dur="4s" repeatCount="indefinite"/>
        </path>
        <path d="M80,420 Q350,140 780,240" stroke="rgba(126,207,194,.1)" stroke-dasharray="4 6">
          <animate attributeName="stroke-dashoffset" from="0" to="-40" dur="5s" repeatCount="indefinite"/>
        </path>
        <path d="M300,400 Q650,100 1100,180" stroke="rgba(240,201,122,.1)" stroke-dasharray="6 4">
          <animate attributeName="stroke-dashoffset" from="0" to="-40" dur="3.5s" repeatCount="indefinite"/>
        </path>
      </g>
      <!-- Noeuds logistiques (villes) -->
      <g>
        <!-- Paris -->
        <circle cx="120" cy="380" r="5" fill="rgba(240,201,122,.7)"/>
        <circle cx="120" cy="380" r="10" fill="rgba(240,201,122,.12)"/>
        <circle cx="120" cy="380" r="18" fill="rgba(240,201,122,.05)">
          <animate attributeName="r" values="18;26;18" dur="3s" repeatCount="indefinite"/>
          <animate attributeName="fill-opacity" values=".08;0;.08" dur="3s" repeatCount="indefinite"/>
        </circle>
        <!-- Lyon -->
        <circle cx="200" cy="320" r="4" fill="rgba(240,201,122,.55)"/>
        <circle cx="200" cy="320" r="8" fill="rgba(240,201,122,.1)"/>
        <!-- Marseille -->
        <circle cx="300" cy="400" r="4" fill="rgba(240,201,122,.5)"/>
        <circle cx="300" cy="400" r="8" fill="rgba(240,201,122,.08)"/>
        <!-- Dakar -->
        <circle cx="860" cy="160" r="5" fill="rgba(126,207,194,.7)"/>
        <circle cx="860" cy="160" r="10" fill="rgba(126,207,194,.12)"/>
        <circle cx="860" cy="160" r="18" fill="rgba(126,207,194,.05)">
          <animate attributeName="r" values="18;28;18" dur="2.5s" repeatCount="indefinite"/>
          <animate attributeName="fill-opacity" values=".08;0;.08" dur="2.5s" repeatCount="indefinite"/>
        </circle>
        <!-- Abidjan -->
        <circle cx="950" cy="200" r="4" fill="rgba(126,207,194,.55)"/>
        <circle cx="950" cy="200" r="8" fill="rgba(126,207,194,.1)"/>
        <!-- Bamako -->
        <circle cx="780" cy="240" r="4" fill="rgba(126,207,194,.5)"/>
        <circle cx="780" cy="240" r="8" fill="rgba(126,207,194,.08)"/>
        <!-- Lagos -->
        <circle cx="1100" cy="180" r="4" fill="rgba(126,207,194,.45)"/>
        <circle cx="1100" cy="180" r="8" fill="rgba(126,207,194,.08)"/>
      </g>
      <!-- Paquets animés sur les routes -->
      <rect width="8" height="6" rx="2" fill="rgba(240,201,122,.85)">
        <animateMotion dur="3s" repeatCount="indefinite" path="M120,380 Q400,80 860,160"/>
      </rect>
      <rect width="6" height="5" rx="1.5" fill="rgba(126,207,194,.75)">
        <animateMotion dur="4s" repeatCount="indefinite" begin="1s" path="M200,320 Q550,60 950,200"/>
      </rect>
      <rect width="7" height="5" rx="2" fill="rgba(240,201,122,.6)">
        <animateMotion dur="3.5s" repeatCount="indefinite" begin="2s" path="M300,400 Q650,100 1100,180"/>
      </rect>
    </svg>
  </div>

  <div class="hero-left rv">
    <div class="hero-tag"><span class="live-dot"></span>Bêta ouverte</div>
    <h1 class="hero-h1">Envoyer un colis<br>n'a jamais été<br><strong>aussi simple.</strong></h1>
    <p class="hero-sub">De Paris à Dakar, de Lyon à Abidjan : des transporteurs vérifiés sur votre route emportent vos colis. Le paiement reste retenu jusqu'à la livraison.</p>
    <div class="hero-btns">
      <button class="btn-hp">Envoyer un colis</button>
      <button class="btn-hs">Proposer un trajet</button>
    </div>
  </div>
  <div class="hero-right-wrap rv d1">
    <div class="hcard">
      <div class="hc-hd">
        <span class="hc-live">Exemple de trajet</span>
        <span class="hc-rlbl">Paris → Dakar</span>
      </div>
      <div class="hc-route">
        <div class="hc-city"><div class="cdot cdot-f"></div><div><div class="cname">Paris CDG</div><div class="csub">France</div></div></div>
        <div class="hc-line"><div class="hc-plane">✈</div></div>
        <div class="hc-city"><div class="cdot cdot-t"></div><div><div class="cname">Dakar DSS</div><div class="csub">Sénégal</div></div></div>
      </div>
      <div class="hc-fields">
        <div class="hc-field"><div class="fl">Départ</div><div class="fv">Mer. 08h15</div></div>
        <div class="hc-field"><div class="fl">Capacité</div><div class="fv">8 kg max</div></div>
        <div class="hc-field"><div class="fl">Par colis</div><div class="fv">15 kg max</div></div>
        <div class="hc-field"><div class="fl">Identité</div><div class="fv">Vérifiée</div></div>
      </div>
      <div class="hc-price"><span class="pl">Tarif fixé par le transporteur</span><span class="pv">8,50 €/kg</span></div>
      <button class="hc-cta">Voir les trajets disponibles →</button>
      <div class="hc-sec">🔒 Paiement retenu par Stripe jusqu'à la livraison</div>
    </div>
  </div>
</div>

<!-- HOW IT WORKS -->
<div class="sec sec-cream" id="how">
  <div class="sechd rv">
    <span class="ey ey-teal">Comment ça marche</span>
    <h2 class="sh2">Simple pour tout le monde.</h2>
    <p class="sp">Deux parcours distincts, une seule plateforme.</p>
  </div>
  <div class="how-toggle rv">
    <button class="tbtn active" id="btnExp"><span class="tico">📦</span>Expéditeur</button>
    <div class="how-sep"></div>
    <button class="tbtn" id="btnTra"><span class="tico">🚗</span>Transporteur</button>
  </div>
  <div class="how-panels rv">
    <div class="how-panel act" id="panelExp">
      <div class="scell"><div class="snum">01</div><div class="sico">📋</div><div class="stit">Publiez votre annonce</div><p class="sdesc">Décrivez votre colis, son poids, la destination et votre délai. Deux minutes suffisent.</p><span class="stag stg-g">2 minutes</span></div>
      <div class="scell"><div class="snum">02</div><div class="sico">🤝</div><div class="stit">Trouvez un transporteur</div><p class="sdesc">L'algorithme vous propose les meilleurs profils disponibles sur votre route, triés par score de confiance.</p><span class="stag stg-t">Matching</span></div>
      <div class="scell"><div class="snum">03</div><div class="sico">📍</div><div class="stit">Suivez votre colis</div><p class="sdesc">Une notification à chaque étape. Le transporteur n'est payé qu'après la livraison confirmée.</p><span class="stag stg-g">Suivi</span></div>
    </div>
    <div class="how-panel" id="panelTra">
      <div class="scell"><div class="snum">01</div><div class="sico">🗺️</div><div class="stit">Proposez un trajet</div><p class="sdesc">Indiquez votre itinéraire, vos dates et la capacité disponible dans vos bagages.</p><span class="stag stg-t">Simple</span></div>
      <div class="scell"><div class="snum">02</div><div class="sico">⚡</div><div class="stit">Optimisez vos déplacements</div><p class="sdesc">Recevez des demandes adaptées à votre route. Acceptez en un clic, à vos propres conditions.</p><span class="stag stg-g">Flexible</span></div>
      <div class="scell"><div class="snum">03</div><div class="sico">💶</div><div class="stit">Gagnez de l'argent</div><p class="sdesc">Versement sur votre compte bancaire 48 h après la livraison confirmée.</p><span class="stag stg-t">Stripe Connect</span></div>
    </div>
  </div>
</div>

<!-- TRAJETS POPULAIRES -->
<div class="routes-sec" id="routes">
  <div class="sechd rv">
    <span class="ey ey-gold">Corridors</span>
    <h2 class="sh2">Vos routes, nos solutions.</h2>
    <p class="sp">Quelques corridors entre l'Europe, l'Afrique et l'Amérique.</p>
  </div>
  <div class="routes-grid-wrap rv">
    <div class="routes-grid">
      <div class="route-card">
        <div class="rc-flag"><div class="flag-circle">🇫🇷</div><div class="rc-countries">France → Sénégal</div></div>
        <div class="rc-route-name">Paris <span class="rc-arrow">→</span> Dakar</div>
        <button class="rc-cta">Voir les trajets disponibles</button>
      </div>
      <div class="route-card">
        <div class="rc-flag"><div class="flag-circle">🇫🇷</div><div class="rc-countries">France → Côte d'Ivoire</div></div>
        <div class="rc-route-name">Lyon <span class="rc-arrow">→</span> Abidjan</div>
        <button class="rc-cta">Voir les trajets disponibles</button>
      </div>
      <div class="route-card">
        <div class="rc-flag"><div class="flag-circle">🇫🇷</div><div class="rc-countries">France → Mali</div></div>
        <div class="rc-route-name">Marseille <span class="rc-arrow">→</span> Bamako</div>
        <button class="rc-cta">Voir les trajets disponibles</button>
      </div>
    </div>
  </div>
  <!-- Carousel supplémentaire -->
  <div class="carousel-belt-label rv">Toutes les destinations →</div>
  <div class="carousel-wrap" id="cWrap">
    <div class="carousel-track" id="cTrack"></div>
  </div>
</div>

<!-- AFRIQUE -->
<div class="afrique" id="afrique">
  <canvas class="af-canvas" id="afCanvas"></canvas>
  <div class="af-inner">
    <div class="rv">
      <span class="ey ey-lt">Colis2Ship · Afrique</span>
      <h2 class="af-h2">La révolution logistique<br>vers <em>l'Afrique.</em></h2>
      <p class="af-p">Des transporteurs qui voyagent déjà entre l'Europe, l'Afrique et l'Amérique emportent vos colis dans leurs bagages.</p>
      <button class="btn-af">Explorer les trajets Afrique →</button>
    </div>
    <div class="af-panel rv d1">
      <div class="af-phd"><span class="af-plbl">Corridors · Europe · Afrique · Amérique</span></div>
      <div class="af-row"><div class="af-flag">🇫🇷→🇸🇳</div><div class="af-inf"><div class="af-rn">Paris → Dakar</div></div></div>
      <div class="af-row"><div class="af-flag">🇫🇷→🇨🇮</div><div class="af-inf"><div class="af-rn">Lyon → Abidjan</div></div></div>
      <div class="af-row"><div class="af-flag">🇧🇪→🇨🇮</div><div class="af-inf"><div class="af-rn">Bruxelles → Abidjan</div></div></div>
      <div class="af-row"><div class="af-flag">🇫🇷→🇲🇱</div><div class="af-inf"><div class="af-rn">Marseille → Bamako</div></div></div>
      <div class="af-row"><div class="af-flag">🇫🇷→🇨🇲</div><div class="af-inf"><div class="af-rn">Paris → Douala</div></div></div>
      <div class="af-row"><div class="af-flag">🇧🇯→🇫🇷</div><div class="af-inf"><div class="af-rn">Cotonou → Marseille</div></div></div>
    </div>
  </div>
</div>

<!-- RÉSEAU DE CONFIANCE -->
<div class="sec sec-cream" id="reseau">
  <div class="sechd rv">
    <span class="ey ey-teal">Un réseau de confiance</span>
    <h2 class="sh2">Votre colis entre de bonnes mains.</h2>
    <p class="sp">Chaque étape de votre envoi est vérifiée.</p>
  </div>
  <div class="reseau-grid rv">
    <div class="reseau-card">
      <div class="reseau-ico-wrap">🛡️</div>
      <div class="reseau-title">Profils vérifiés</div>
      <p class="reseau-desc">Chaque membre vérifie son e-mail, son téléphone et sa pièce d'identité avant de publier ou de proposer.</p>
      <span class="reseau-stat">E-mail · téléphone · identité</span>
    </div>
    <div class="reseau-card">
      <div class="reseau-ico-wrap">🔒</div>
      <div class="reseau-title">Paiement sécurisé</div>
      <p class="reseau-desc">Débité à l'accord du transporteur, versé seulement après la livraison confirmée.</p>
      <span class="reseau-stat">Paiement par Stripe</span>
    </div>
    <div class="reseau-card">
      <div class="reseau-ico-wrap">💬</div>
      <div class="reseau-title">Un recours en cas de problème</div>
      <p class="reseau-desc">Après la livraison, vous avez 48 heures pour ouvrir un litige, examiné par l'équipe Colis2Ship.</p>
      <span class="reseau-stat">48 h pour réagir</span>
    </div>
    <div class="reseau-card">
      <div class="reseau-ico-wrap">📦</div>
      <div class="reseau-title">Une remise prouvée</div>
      <p class="reseau-desc">Le transporteur scanne votre code unique au moment de la remise : elle est enregistrée.</p>
      <span class="reseau-stat">Code QR unique</span>
    </div>
  </div>
</div>

<!-- FOOTER -->
<div class="footer">
  <div class="footer-top">
    <div>
      <div class="fbrand"><div class="logo-mark" style="width:20px;height:20px;font-size:9px">C2</div>Colis<em>2</em>Ship</div>
      <div class="fdesc">Des colis confiés à des transporteurs vérifiés, entre l'Europe, l'Afrique et l'Amérique.</div>
    </div>
    <div class="fcol"><h5>Produit</h5><a href="#" class="nav-link">Comment ça marche</a><a href="#" class="nav-link">Trajets</a><a href="#" class="nav-link">Afrique</a></div>
  </div>
  <div class="footer-bot">
    <span class="fcopy">© 2026 Colis2Ship</span>
  </div>
</div>`;

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Lu au clic : le compte peut se charger après la mise en place des écouteurs
  const userRef = useRef(user);
  userRef.current = user;
  // En-tête : « Connexion » ou « Mon espace » et le bouton de publication selon le rôle, rendus par React dans la
  // maquette (le texte de la maquette était figé : « Connexion », « Publier un colis » pour tout le monde)
  const [headerSlot, setHeaderSlot] = useState(null);
  // La maquette n'est écrite qu'une fois : sans ce mémo, chaque rendu (compte chargé, en-tête monté) la réécrivait et
  // emportait les boutons de l'en-tête avec l'ancien exemplaire
  const landing = useMemo(() => (
    <div id="landing-root" data-testid="landing-page" dangerouslySetInnerHTML={{ __html: LANDING_HTML }} />
  ), []);

  useEffect(() => {
    // Initialise toutes les animations de la maquette (carousels, reveals, etc.)
    let cleanup = null;
    try {
      cleanup = initLandingAnim();
    } catch (e) {
      console.error('[landing] init error', e);
    }

    // Intercepte les CTA pour router React (au lieu de href="#")
    const root = document.getElementById('landing-root');
    if (!root) return;

    setHeaderSlot(root.querySelector('[data-landing-actions]'));

    const handleClick = (e) => {
      const target = e.target.closest('button, a');
      if (!target) return;
      // Boutons de l'en-tête rendus par React : ils gèrent leur clic eux-mêmes
      if (target.closest('[data-landing-actions]')) return;
      const text = (target.textContent || '').trim().toLowerCase();
      const cls = target.className || '';

      // Connexion → /login
      if (text.includes('connexion') || cls.includes('btn-gn')) {
        e.preventDefault();
        navigate('/login');
        return;
      }
      // Publier un colis / Envoyer un colis → inscription (visiteur), formulaire ou demande du rôle (utils/roles.js)
      if (text.includes('publier un colis') || text.includes('envoyer un colis')) {
        e.preventDefault();
        navigate(publishTarget(userRef.current, 'colis'));
        return;
      }
      // Proposer un trajet → idem côté trajet
      if (text.includes('proposer un trajet')) {
        e.preventDefault();
        navigate(publishTarget(userRef.current, 'trajet'));
        return;
      }
      // Réserver ce trajet → /login (puis dashboard)
      if (text.includes('réserver ce trajet')) {
        e.preventDefault();
        navigate('/annonces');
        return;
      }
      // Voir les trajets disponibles → /annonces
      if (text.includes('voir les trajets')) {
        e.preventDefault();
        navigate('/annonces');
        return;
      }
      // Explorer trajets Afrique → /annonces
      if (text.includes('explorer les trajets')) {
        e.preventDefault();
        navigate('/annonces');
        return;
      }
      // Nous rejoindre (agences) → côté trajet
      if (text.includes('nous rejoindre')) {
        e.preventDefault();
        navigate(publishTarget(userRef.current, 'trajet'));
        return;
      }
      // Nav links: smooth scroll to sections
      if (cls.includes('nav-link')) {
        e.preventDefault();
        const txt = text;
        let id = null;
        if (txt.includes('accueil')) id = null; // top
        else if (txt.includes('comment')) id = 'how';
        else if (txt.includes('annonces') || txt.includes('trajets disponibles') || txt === 'trajets') {
          // Direct route /annonces
          navigate('/annonces');
          return;
        }
        else if (txt.includes('trajets')) id = 'routes';
        else if (txt.includes('afrique')) id = 'afrique';
        else if (txt.includes('réseau')) id = 'reseau';
        else if (txt.includes('télécharger')) id = 'app';

        if (id === null) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
      // Logo → top
      if (cls.includes('logo')) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    root.addEventListener('click', handleClick);
    return () => {
      root.removeEventListener('click', handleClick);
      if (typeof cleanup === 'function') {
        try { cleanup(); } catch (e) { /* noop */ }
      }
    };
  }, [navigate]);

  return (
    <>
      {landing}
      {headerSlot && createPortal(
        <>
          {user
            ? <button type="button" className="btn-gn" onClick={() => navigate(user.roles?.includes('ROLE_ADMIN') ? '/admin/dashboard' : '/app/dashboard')} data-testid="landing-my-space">Mon espace</button>
            : <button type="button" className="btn-gn" onClick={() => navigate('/login')} data-testid="landing-login">Connexion</button>}
          <PublishButton user={user} buttonClassName="btn-cn" testid="landing-publish" />
        </>,
        headerSlot,
      )}
    </>
  );
}
